import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, formatError } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Zap, Eye, EyeOff } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import SEO from '../components/SEO';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage({ mode: modeProp }) {
  const location = useLocation();
  const defaultMode = modeProp || (location.pathname === '/kayit' ? 'register' : 'login');
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register, checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleGoogle = async (credentialResponse) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/google`,
        { credential: credentialResponse.credential },
        { withCredentials: true }
      );
      if (checkAuth) await checkAuth();
      toast.success('Hoş geldiniz!');
      navigate('/');
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Hoş geldiniz!');
      } else {
        await register(email, username, password, displayName);
        toast.success('Hesabınız oluşturuldu!');
      }
      navigate('/');
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10 overflow-hidden bg-[#061a12]">
      <img src="/img/hero-l7e.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#061a12]/70 via-[#061a12]/60 to-[#061a12]" />
      <SEO title={mode === 'login' ? 'Giriş Yap' : 'Kayıt Ol'} noindex />
      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-white/95 backdrop-blur rounded-3xl shadow-2xl border border-white/40 p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 bg-emerald-600 rounded-2xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="font-extrabold text-2xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mini<span className="text-emerald-600">Oto</span>
            </span>
          </div>

          {/* Toggle */}
          <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              data-testid="auth-login-tab"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                mode === 'login' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Giriş Yap
            </button>
            <button
              onClick={() => setMode('register')}
              data-testid="auth-register-tab"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                mode === 'register' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Kayıt Ol
            </button>
          </div>

          {/* Google Sign-In */}
          <div className="mb-4" data-testid="google-signin-wrap">
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogle}
                onError={() => toast.error('Google ile giriş başarısız')}
                useOneTap={false}
                shape="rectangular"
                text={mode === 'login' ? 'signin_with' : 'signup_with'}
                locale="tr"
                width="320"
              />
            </div>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400">veya email ile</span></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="ornek@email.com"
                required
                className="mt-1"
                data-testid="auth-email-input"
              />
            </div>

            {mode === 'register' && (
              <>
                <div>
                  <Label htmlFor="username" className="text-slate-700 font-medium">Kullanıcı Adı</Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="kullanici_adi"
                    required
                    className="mt-1"
                    data-testid="auth-username-input"
                  />
                </div>
                <div>
                  <Label htmlFor="displayName" className="text-slate-700 font-medium">Ad Soyad</Label>
                  <Input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="Adınız Soyadınız"
                    className="mt-1"
                    data-testid="auth-displayname-input"
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Şifre</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="En az 6 karakter"
                  required
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl mt-2"
              data-testid="auth-submit-btn"
            >
              {loading ? 'Yükleniyor...' : mode === 'login' ? 'Giriş Yap' : 'Hesap Oluştur'}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === 'login' ? (
              <>Hesabınız yok mu? <button onClick={() => setMode('register')} className="text-emerald-600 font-medium hover:underline">Kayıt Ol</button></>
            ) : (
              <>Zaten hesabınız var mı? <button onClick={() => setMode('login')} className="text-emerald-600 font-medium hover:underline">Giriş Yap</button></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
